import { Component, OnInit } from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormsModule } from '@angular/forms';
import { ApplicationDataService } from '../../../core/services/application-data.service';

interface AssistLog {
  complaintNo: string;
  createdAt: string;
  details: string;
  userId?: number;
  username?: string;
}

interface ChatMessage {
  sender: 'bot' | 'user';
  text: string;
}

@Component({
  selector: 'app-complaint-assist',
  standalone: true,
  imports: [CommonModule, FormsModule],
  templateUrl: './complaint-assist.component.html',
  styleUrl: './complaint-assist.component.css'
})
export class ComplaintAssistComponent implements OnInit {
  logs: AssistLog[] = [];
  isLoading = true;
  fromDate = '';
  toDate = '';
  dateError = '';
  skeletonRows = Array.from({ length: 8 });

  currentPage = 1;
  pageSize = 10;
  pageSizeOptions = [10, 25, 50, 100];

  get pagedLogs(): AssistLog[] {
    const start = (this.currentPage - 1) * this.pageSize;
    return this.logs.slice(start, start + this.pageSize);
  }

  get totalPages(): number {
    return Math.max(1, Math.ceil(this.logs.length / this.pageSize));
  }

  prevPage(): void {
    if (this.currentPage > 1) this.currentPage--;
  }

  nextPage(): void {
    if (this.currentPage < this.totalPages) this.currentPage++;
  }

  onPageSizeChange(): void {
    this.currentPage = 1;
  }

  showModal = false;
  selectedLog: AssistLog | null = null;
  parsedMessages: ChatMessage[] = [];

  constructor(private dataService: ApplicationDataService) {}

  ngOnInit(): void {
    this.loadLogs();
  }

  loadLogs(): void {
    this.isLoading = true;
    this.currentPage = 1;
    this.dataService.getChatbotAssistLogs(this.fromDate || undefined, this.toDate || undefined).subscribe({
      next: (res: any) => {
        const list = Array.isArray(res) ? res : res.data || [];
        this.logs = list;
        this.isLoading = false;
      },
      error: () => {
        this.logs = [];
        this.isLoading = false;
      }
    });
  }

  validateDates(): boolean {
    if (this.fromDate && this.toDate && this.fromDate > this.toDate) {
      this.dateError = 'Date From cannot be greater than Date To.';
      return false;
    }
    this.dateError = '';
    return true;
  }

  applyFilters(): void {
    if (!this.validateDates()) return;
    this.loadLogs();
  }

  clearFilters(): void {
    this.fromDate = '';
    this.toDate = '';
    this.dateError = '';
    this.loadLogs();
  }

  openModal(log: AssistLog): void {
    this.selectedLog = log;
    this.parsedMessages = this.parseDetails(log.details);
    this.showModal = true;
  }

  closeModal(): void {
    this.showModal = false;
    this.selectedLog = null;
    this.parsedMessages = [];
  }

  parseDetails(details: string): ChatMessage[] {
    if (!details) return [];
    return details
      .split('\n')
      .filter(line => line.trim())
      .map(line => {
        if (line.startsWith('[Bot]:')) {
          return { sender: 'bot' as const, text: line.replace('[Bot]:', '').trim() };
        } else if (line.startsWith('[User]:')) {
          return { sender: 'user' as const, text: line.replace('[User]:', '').trim() };
        }
        return null;
      })
      .filter((m): m is ChatMessage => m !== null);
  }

  formatDate(iso: string): string {
    if (!iso) return '';
    const d = new Date(iso);
    const dd = String(d.getDate()).padStart(2, '0');
    const mm = String(d.getMonth() + 1).padStart(2, '0');
    const yyyy = d.getFullYear();
    const hh = String(d.getHours()).padStart(2, '0');
    const min = String(d.getMinutes()).padStart(2, '0');
    return `${dd}/${mm}/${yyyy} ${hh}:${min}`;
  }
}
